import type { DepartmentRow } from '../../lib/database.types'

/**
 * Departments are data, not enums (product rule 2).
 *
 * The database stores slugs -- 'dev', 'data'. The design shows "Development",
 * "Data Management". The map between them is a table, `public.departments`,
 * read through `useDepartments()`: a lookup written here in the front end would
 * be exactly the hardcoded list the rule forbids, and it would be wrong the day
 * a fifth department appears.
 *
 * Two things follow from the table having no foreign key onto the slugs in
 * agent_runs, events and approvals. A slug with no row still renders, verbatim,
 * and stays filterable -- the same rule the approvals mapper follows for a
 * `context` shape it does not recognise, one level up. And the list is the
 * table's rows rather than the slugs a query happened to return, so a
 * department that has gone quiet keeps its place instead of silently
 * disappearing, which would read as a lane with nothing to show.
 */

export interface Department {
  slug: string
  label: string
}

/** Display name from the table, or the slug itself if the table has no row for it. */
export function departmentLabel(slug: string, departments: ReadonlyArray<DepartmentRow> = []): string {
  return departments.find((d) => d.slug === slug)?.display_name ?? slug
}

/**
 * The department list, in the order the table asks for: sort_order, not
 * alphabetical, because "Development" before "Data Management" is the fleet's
 * ordering and not one this app gets to invent.
 *
 * Any slug the rows carry that the table does not know is appended, labelled
 * verbatim, so an unrecognised department is still visible and still filterable.
 */
export function departmentsFrom(
  departments: ReadonlyArray<DepartmentRow>,
  rows: ReadonlyArray<{ department: string }> = [],
): Department[] {
  const known = [...departments]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((d) => ({ slug: d.slug, label: d.display_name }))
  const unknown = new Set<string>()
  for (const row of rows) {
    if (row.department && !departments.some((d) => d.slug === row.department)) unknown.add(row.department)
  }
  return [...known, ...[...unknown].sort((a, b) => a.localeCompare(b)).map((slug) => ({ slug, label: slug }))]
}
